'use client';

import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, LockKeyhole, Mail, ShieldCheck } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';

import { WIDGETS_ASSET_HOST } from '@/shared/config/assets';
import { Checkbox, FieldError, Input } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';

import { FormShell } from '../shared/FormShell';
import { FormToastViewport, useFormSubmissionToast } from '../shared/FormSubmissionToast';
import { SubmitButton } from '../shared/SubmitButton';
import { submitForm } from '../shared/configurable/mock-submit';
import type { FormSubmitPayload, TechnicalStatus } from '../shared/configurable';
import { loginFormMockValues } from './login-form.mock';
import { loginFormSchema, type LoginFormValues } from './login-form.schema';
import type { LoginFormProps, LoginSocialProvider } from './login-form.types';

const DEFAULT_LOGIN_TITLE = 'Iniciar sesión';
const DEFAULT_LOGIN_SUBTITLE = 'Continúa tu formación desde donde la dejaste.';
const DEFAULT_REGISTER_URL = 'https://adipa.cl/registro';
const DEFAULT_FORGOT_PASSWORD_URL = 'https://adipa.cl/recuperar-contrasena';
const SOCIAL_PROVIDER_ICON_SRC: Record<string, string> = {
  google: `${WIDGETS_ASSET_HOST}/images/auth/google.svg`,
};

export function LoginForm({
  platformCountry = 'CL',
  pill,
  title = DEFAULT_LOGIN_TITLE,
  subtitle = DEFAULT_LOGIN_SUBTITLE,
  securityText,
  naked = false,
  nativeAction,
  credentialId,
  defaultEmail,
  defaultRemember,
  errorText,
  submitUrl,
  registerUrl = DEFAULT_REGISTER_URL,
  forgotPasswordUrl = DEFAULT_FORGOT_PASSWORD_URL,
  socialProviders = [],
  socialDividerLabel = 'o ingresa con',
  source,
  trackingId,
  pageUrl,
  scenario = 'empty',
  mockResult = 'success',
  requireSubmitUrl = false,
  titleAlign = 'center',
  buttonWidth = 'full',
  onSuccess,
  onError,
  onSubmitPayload,
  onValidationError,
  onSocialAuth,
}: LoginFormProps) {
  const [status, setStatus] = useState<TechnicalStatus>('idle');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const defaultValues = useMemo(
    () => ({
      ...loginFormMockValues[scenario],
      ...(defaultEmail !== undefined ? { email: defaultEmail } : {}),
      ...(defaultRemember !== undefined ? { remember: defaultRemember } : {}),
    }),
    [scenario, defaultEmail, defaultRemember],
  );
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    getValues,
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
  });
  const values = useWatch({ control });
  const isSubmittable = loginFormSchema.safeParse(values).success;
  useFormSubmissionToast({
    status,
    success: {
      title: '¡Sesión iniciada!',
      message: 'Tus credenciales fueron enviadas correctamente.',
    },
    error: {
      title: 'No pudimos iniciar sesión',
      message: 'Revisa tus datos o intenta nuevamente en unos segundos.',
    },
  });

  useEffect(() => {
    reset(defaultValues);
    setStatus('idle');
  }, [defaultValues, reset]);

  const onValidSubmit = async (formValues: LoginFormValues) => {
    if (requireSubmitUrl && !submitUrl) {
      const error = new Error('Missing required submit-url attribute for adipa-form-widget.');
      setStatus('error');
      onError?.(error);
      return;
    }

    const payload = createLoginPayload(formValues, {
      pageUrl,
      platformCountry,
      source,
      trackingId,
    });

    setStatus('submitting');
    onSubmitPayload?.(payload);

    try {
      const response = await submitForm(payload, { submitUrl, result: mockResult });
      setStatus('success');
      onSuccess?.({ payload, response });
    } catch (err) {
      setStatus('error');
      onError?.(err);
    }
  };

  const onInvalidSubmit = (error: unknown) => {
    setStatus('idle');
    onValidationError?.(error);
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (nativeAction) {
      event.preventDefault();
      setStatus('validating');
      void handleSubmit(() => {
        setStatus('submitting');
        const values = getValues();
        const form = document.createElement('form');
        form.method = 'post';
        form.action = nativeAction;

        const addInput = (name: string, value: string) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        };

        addInput('username', values.email);
        addInput('password', values.password);
        if (values.remember) addInput('rememberMe', 'on');
        if (credentialId) addInput('credentialId', credentialId);

        document.body.appendChild(form);
        window.setTimeout(() => form.submit(), 0);
      }, onInvalidSubmit)(event);
      return;
    }

    setStatus('validating');
    void handleSubmit(onValidSubmit, onInvalidSubmit)(event);
  };

  const isLoading = status === 'validating' || status === 'submitting';

  return (
    <div className="w-full">
      <FormToastViewport />
      <FormShell naked={naked} className="mx-auto max-w-md p-4 sm:p-6">
        <header className={cn('pb-6', titleAlign === 'center' ? 'text-center' : 'text-left')}>
          {pill && (
            <div className={cn('mb-4', titleAlign === 'center' ? 'flex justify-center' : '')}>
              <span className="border-adipa-primary text-adipa-primary inline-block rounded-full border px-5 py-1.5 text-xs font-semibold uppercase tracking-wider">
                {pill}
              </span>
            </div>
          )}
          <h2 className="text-adipa-text-primary text-2xl font-bold leading-tight">{title}</h2>
          <p className="text-adipa-text-secondary mt-2 text-sm leading-6">{subtitle}</p>
        </header>

        <form onSubmit={handleFormSubmit} noValidate>
          <div className="space-y-4">
            <LoginField
              id="adipa-login-email"
              label="Email"
              error={errors.email?.message}
              icon={<Mail className="h-4 w-4" aria-hidden="true" />}
            >
              <Input
                id="adipa-login-email"
                type="email"
                placeholder="tu@email.com"
                autoComplete="email"
                inputMode="email"
                hasError={Boolean(errors.email)}
                className="h-11 pl-10"
                aria-describedby="adipa-login-email-error"
                disabled={isLoading}
                {...register('email')}
              />
            </LoginField>

            <LoginField
              id="adipa-login-password"
              label="Contraseña"
              error={errors.password?.message}
              icon={<LockKeyhole className="h-4 w-4" aria-hidden="true" />}
            >
              <Input
                id="adipa-login-password"
                type={isPasswordVisible ? 'text' : 'password'}
                placeholder="••••••••"
                autoComplete="current-password"
                hasError={Boolean(errors.password)}
                className="h-11 px-10"
                aria-describedby="adipa-login-password-error"
                disabled={isLoading}
                {...register('password')}
              />
              <button
                type="button"
                disabled={isLoading}
                className="text-adipa-text-secondary duration-adipa-fast hover:text-adipa-primary focus-visible:ring-adipa-focus-ring absolute inset-y-0 right-3 flex items-center transition-colors focus-visible:outline-none focus-visible:ring-2"
                onClick={() => setIsPasswordVisible((current) => !current)}
                aria-label={isPasswordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {isPasswordVisible ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            </LoginField>

            <div className="flex items-center justify-between gap-3">
              <Checkbox
                id="adipa-login-remember"
                label="Recordarme"
                disabled={isLoading}
                {...register('remember')}
              />
              <a
                href={forgotPasswordUrl}
                className="text-adipa-primary text-right text-xs font-medium underline underline-offset-4 hover:no-underline"
              >
                Olvidé mi contraseña
              </a>
            </div>
          </div>

          {errorText && (
            <div
              className="rounded-adipa-md border-adipa-error/40 bg-adipa-error-surface text-adipa-error mt-4 border px-3 py-2.5 text-sm"
              role="alert"
              dangerouslySetInnerHTML={{ __html: errorText }}
            />
          )}

          <div className="mt-4">
            <SubmitButton
              loading={isLoading}
              disabled={!isSubmittable || isLoading}
              label="Ingresar"
              loadingLabel="Ingresando..."
              className={buttonWidth === 'full' ? 'w-full' : 'w-auto min-w-44'}
            />
          </div>

          {securityText && (
            <div className="text-adipa-text-secondary mt-4 flex items-center justify-center gap-2 text-xs">
              <ShieldCheck
                className="text-adipa-primary h-4 w-4 flex-shrink-0"
                aria-hidden="true"
              />
              <span>{securityText}</span>
            </div>
          )}
        </form>

        {socialProviders.length > 0 && (
          <SocialAuthSection
            dividerLabel={socialDividerLabel}
            providers={socialProviders}
            onSocialAuth={onSocialAuth}
          />
        )}
      </FormShell>

      <p className="text-adipa-text-primary mx-auto mt-2 max-w-md text-center text-sm leading-6">
        <span className="font-semibold">¿No tienes cuenta?</span>{' '}
        <a
          href={registerUrl}
          className="text-adipa-primary font-medium underline underline-offset-4 hover:no-underline"
        >
          Regístrate
        </a>
      </p>
    </div>
  );
}

function LoginField({
  children,
  error,
  icon,
  id,
  label,
}: {
  children: ReactNode;
  error?: string;
  icon: ReactNode;
  id: string;
  label: string;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <label htmlFor={id} className="text-adipa-text-primary text-sm font-medium">
          {label}
        </label>
      </div>
      <div className="relative">
        <span className="text-adipa-text-secondary pointer-events-none absolute inset-y-0 left-3 flex items-center">
          {icon}
        </span>
        {children}
      </div>
      <FieldError id={`${id}-error`} message={error} className="mt-1" />
    </div>
  );
}

function SocialAuthSection({
  dividerLabel,
  onSocialAuth,
  providers,
}: {
  dividerLabel: string;
  onSocialAuth?: (provider: LoginSocialProvider) => void;
  providers: LoginSocialProvider[];
}) {
  return (
    <div className="mt-6">
      <div className="flex items-center gap-3" aria-hidden="true">
        <span className="bg-adipa-border h-px flex-1" />
        <span className="text-adipa-text-secondary text-sm font-medium">{dividerLabel}</span>
        <span className="bg-adipa-border h-px flex-1" />
      </div>

      <div className="mt-5 grid gap-3">
        {providers.map((provider) => (
          <SocialProviderButton key={provider.id} provider={provider} onSocialAuth={onSocialAuth} />
        ))}
      </div>
    </div>
  );
}

function SocialProviderButton({
  onSocialAuth,
  provider,
}: {
  onSocialAuth?: (provider: LoginSocialProvider) => void;
  provider: LoginSocialProvider;
}) {
  const content = (
    <>
      <ProviderIcon provider={provider} />
      <span>{provider.label}</span>
    </>
  );
  const className = [
    'inline-flex h-14 w-full items-center justify-center gap-3 rounded-adipa-lg px-4',
    'border border-adipa-primary bg-adipa-surface text-base font-semibold text-adipa-primary',
    'transition-[background-color,border-color,color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
    'hover:bg-adipa-surface-muted hover:shadow-adipa-console-panel',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-adipa-primary',
    'motion-reduce:transition-none',
  ].join(' ');

  if (provider.authUrl) {
    return (
      <a href={provider.authUrl} className={className} onClick={() => onSocialAuth?.(provider)}>
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={cn(className, 'cursor-pointer')}
      onClick={() => onSocialAuth?.(provider)}
    >
      {content}
    </button>
  );
}

function ProviderIcon({ provider }: { provider: LoginSocialProvider }) {
  if (provider.id === 'google') {
    return <ProviderImageIcon src={SOCIAL_PROVIDER_ICON_SRC.google} />;
  }

  const iconSrc = SOCIAL_PROVIDER_ICON_SRC[provider.id];
  if (iconSrc) {
    return <ProviderImageIcon src={iconSrc} />;
  }

  return (
    <span
      className="rounded-adipa-full border-adipa-border text-adipa-primary flex h-6 w-6 items-center justify-center border text-sm font-bold"
      aria-hidden="true"
    >
      {provider.label.charAt(0).toUpperCase()}
    </span>
  );
}

function ProviderImageIcon({ src }: { src: string }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element -- Icons must load from WIDGETS_ASSET_HOST inside legacy embeds.
    <img src={src} alt="" className="h-6 w-6" aria-hidden="true" loading="lazy" decoding="async" />
  );
}

function createLoginPayload(
  values: LoginFormValues,
  options: {
    pageUrl?: string;
    platformCountry: NonNullable<LoginFormProps['platformCountry']>;
    source?: string;
    trackingId?: string;
  },
): FormSubmitPayload {
  return {
    platformCountry: options.platformCountry,
    variant: 'login',
    ...(options.source ? { source: options.source } : {}),
    ...(options.trackingId ? { trackingId: options.trackingId } : {}),
    ...(options.pageUrl ? { pageUrl: options.pageUrl } : {}),
    fields: {
      email: values.email,
      password: values.password,
      remember: values.remember,
    },
    submittedAt: new Date().toISOString(),
  };
}
